import { useState, useEffect, useRef, useCallback } from 'react';

const emptyData = { results: [], count: 0, next: null, previous: null };

export default function usePaginatedData(
  serviceFunction,
  { limit = 10, extraPromise, query = '' } = {}
) {
  const cache = useRef({});
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const offset = (currentPage - 1) * limit;
  const totalPages = Math.ceil(data.count / limit);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `${query}:${offset}`;
    const queryString = (pageOffset) =>
      `?limit=${limit}&offset=${pageOffset}${query ? `&search=${encodeURIComponent(query)}` : ''}`;
    const normalize = (response) => ({
      results: response.results || [],
      count: response.count || 0,
      next: response.next,
      previous: response.previous,
    });

    async function fetchData() {
      try {
        if (extraPromise) await extraPromise;
        if (cancelled) return;

        let page = cache.current[cacheKey];
        if (!page) {
          setLoading(true);
          page = normalize(await serviceFunction(queryString(offset)));
          if (cancelled) return;
          cache.current[cacheKey] = page;
        }
        setData(page);
        setLoading(false);

        if (currentPage * limit < page.count) {
          const nextOffset = currentPage * limit;
          const nextKey = `${query}:${nextOffset}`;
          if (!cache.current[nextKey]) {
            const nextPage = normalize(await serviceFunction(queryString(nextOffset)));
            if (!cancelled) cache.current[nextKey] = nextPage;
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [currentPage, limit, extraPromise, serviceFunction, query, offset]);

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);
  const startIndex = offset + 1;
  const endIndex = offset + data.results.length;

  return { data, loading, currentPage, totalPages, handlePageChange, startIndex, endIndex };
}
